import { IFieldRequest } from "./fieldRequest";
import { ILenderApplication } from "./lenderApplication";

export enum LenderResponseTypeEnum {
  UNCONDITIONAL_BID = "UNCONDITIONAL_BID",
  CONDITIONAL_BID = "CONDITIONAL_BID",
}

export enum LenderResponseBenefitTypeEnum {
  NO_ADDITIONS_REQUIRED = "NO_ADDITIONS_REQUIRED",
  REDUCED_GUARANTOR_COMMITMENT = "REDUCED_GUARANTOR_COMMITMENT",
  NO_GUARANTOR_REQUIRED = "NO_GUARANTOR_REQUIRED",
  FREE_EARLY_PAYMENT = "FREE_EARLY_PAYMENT",
  NO_LEASING_DOWN_PAYMENT = "NO_LEASING_DOWN_PAYMENT",
  NO_LIMIT_FEE = "NO_LIMIT_FEE",
  NO_SETUP_FEE = "NO_SETUP_FEE",
  EU_LOAN = "EU_LOAN",
  NO_WITHDRAWAL_FEE = "NO_WITHDRAWAL_FEE",
  NO_RECOURSE = "NO_RECOURSE",
  NO_AMORTIZATION = "NO_AMORTIZATION",
}

export interface ILenderResponseBenefit {
  icon?: string;
  displayName: string;
  explanation: string;
  type: LenderResponseBenefitTypeEnum;
}

export interface ILenderResponse {
  id: string;
  bid: ICompanyLoanBid;
  invoicePurchaseBid: IInvoicePurchaseBid;
  leasingBid: ILeasingBid;
  realEstateBid: IRealEstateBid;
  revolvingCreditBid: IRevolvingCreditBid;
  type: LenderResponseTypeEnum;
  benefits: ILenderResponseBenefit[];
  attractivenessScore?: number;
  fieldRequests: IFieldRequest[];
  lenderApplication: ILenderApplication;
  isRecommendedSelect: boolean;
  paidOutAt: string;
  eligibleForPaymentAt: string;
  signingUrl: string;
}

export interface ICompanyLoanBid {
  createdAt: string;
  id: string;
  monthlyCostWithoutSetupFee: number;
  amortizationMethodDisplayName: string;
  amortizationMethodDescription: string;
  totalLoanCost: number;
  monthlyCost: number;
  setupFee: number;
  postalFee: number;
  directDebitFee: number;
  paybackTimeMonths: number;
  amortizationMethod: string;
  lenderMonthlyInterest: number;
  earlyRepaymentFee: number;
  amount: number;
  lenderResponse: ILenderResponse;
  firstMonthInterestRate: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthCost: number;
}

export interface IInvoicePurchaseBid {
  createdAt: string;
  id: string;
  totalPaidOut: number;
  purchasedInvoiceAmount: number;
  commissionPercent: number;
  handlingFeePerInvoice: number;
  monthlyFee: number;
  purchasedAmountPercent: number;
  totalMonthlyCost: number;
  purchaseLimit: number;
  limitFeePerYearPercent: number;
  setupFee: number;
  withRegress: boolean;
  regressNumDaysAfterDueDate: number;
  paidOutPerInvoice: number;
  paidOutPerInvoicePercent: number;
  lenderResponse: ILenderResponse;
  firstMonthInterestRate: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthCost: number;
}

export interface ILeasingBid {
  createdAt: string;
  id: string;
  amount: number;
  paybackTimeMonths: number;
  monthlyPayment: number;
  totalCost: number;
  downPayment: number;
  terminalValue: number;
  setupFee: number;
  contractType: string;
  postalFee: number;
  insuranceMonthlyCost: number;
  lenderResponse: ILenderResponse;
  firstMonthInterestRate: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthCost: number;
}

export interface IRealEstateBid {
  createdAt: string;
  id: string;
  monthlyCostWithoutSetupFee: number;
  amortizationMethodDisplayName: string;
  amortizationMethodDescription: string;
  totalLoanCost: number;
  monthlyCost: number;
  setupFee: number;
  postalFee: number;
  directDebitFee: number;
  paybackTimeMonths: number;
  amortizationMethod: string;
  lenderMonthlyInterest: number;
  earlyRepaymentFee: number;
  amount: number;
  loanToValueRatio: number;
  isRepayableWithoutFee: boolean;
  lenderResponse: ILenderResponse;
  firstMonthInterestRate: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthCost: number;
}

export interface IRevolvingCreditBid {
  createdAt: string;
  id: string;
  earlyRepaymentFee: number;
  directDebitFee: number;
  amount: number;
  monthlyAmortisation: number;
  lenderMonthlyInterest: number;
  withdrawalFee: number;
  setupFee: number;
  limitFeePerYear: number;
  postalFee: number;
  monthlyPayment: number;
  monthlyCost: number;
  lenderResponse: ILenderResponse;
  firstMonthInterestRate: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthCost: number;
}

export interface IBids {
  id: string;
  recommendedLenderResponse?: { id: string };
  invoicePurchaseBids: IInvoicePurchaseBid[];
  companyLoanBids: ICompanyLoanBid[];
  realEstateBids: IRealEstateBid[];
  leasingBids: ILeasingBid[];
  revolvingCreditBids: IRevolvingCreditBid[];
}

export type IResolvedBid =
  | ICompanyLoanBid
  | IInvoicePurchaseBid
  | ILeasingBid
  | IRealEstateBid
  | IRevolvingCreditBid;
